import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** 1× at 1080p / typical PC present. Up to 2× at 4K so wall type stays readable. */
export function presentScaleFromWidth(width: number): number {
  return Math.min(2, Math.max(1, width / 1920));
}

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => void | Promise<void>;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void | Promise<void>;
};

export function fullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/** Must run in a user gesture. Bookmarking `?present=1` cannot start fullscreen. */
export async function requestPresentFullscreen(): Promise<void> {
  if (fullscreenElement()) return;
  const root = document.documentElement as FullscreenElement;
  const request = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  if (!request) return;
  try {
    await request();
  } catch {
    // Blocked when there is no user gesture, or the browser denied the request.
  }
}

export async function exitPresentFullscreen(): Promise<void> {
  if (!fullscreenElement()) return;
  const doc = document as FullscreenDocument;
  const exit = document.exitFullscreen?.bind(document) ?? doc.webkitExitFullscreen?.bind(doc);
  if (!exit) return;
  try {
    await exit();
  } catch {
    // Already left via Esc or the browser chrome.
  }
}

/** Wall / TV mode. Driven by `?present=1` so a display can bookmark the
 *  chrome-free URL. This is not sidebar collapse — the nav is hidden. */
export function usePresentMode() {
  const [searchParams, setSearchParams] = useSearchParams();
  const present = searchParams.get("present") === "1";

  const setPresent = useCallback(
    (value: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("present", "1");
          else next.delete("present");
          return next;
        },
        { replace: true }
      );
      if (value) void requestPresentFullscreen();
      else void exitPresentFullscreen();
    },
    [setSearchParams]
  );

  return { present, setPresent };
}

export function usePresentScale(): number {
  const { present } = usePresentMode();
  const [scale, setScale] = useState(() =>
    typeof window === "undefined" ? 1 : presentScaleFromWidth(window.innerWidth)
  );

  useEffect(() => {
    if (!present) {
      setScale(1);
      return;
    }
    const sync = () => setScale(presentScaleFromWidth(window.innerWidth));
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [present]);

  return present ? scale : 1;
}
